import { Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';

/**
 * Directive that prevents event propagation for specified event types.
 * 
 * This directive provides a flexible way to stop event bubbling and immediate
 * propagation for various DOM events. It supports multiple event types through
 * a comma-separated input and automatically handles event listener management
 * with proper cleanup on destruction.
 * 
 * @implements OnInit - Initializes event listeners
 * @implements OnDestroy - Cleans up event listeners and renderer
 * 
 * @example
 * ```html
 * <!-- Stop click event propagation -->
 * <button [appStopPropagation]="'click'">Click Me</button>
 * 
 * <!-- Stop multiple event types -->
 * <div [appStopPropagation]="'click,mousedown,touchstart'">Interactive Element</div>
 * 
 * <!-- Default behavior (stops click events) -->
 * <span appStopPropagation>Clickable Text</span>
 * ```
 */
@Directive({
  selector: '[appStopPropagation]'
})
export class StopPropagationDirective implements OnInit, OnDestroy {

  /**
   * Input property that specifies which events should have propagation stopped.
   * Accepts a comma-separated string of event types. If not provided or empty,
   * defaults to 'click' events.
   * 
   * @type {string}
   * @default 'click'
   * 
   * @example
   * ```typescript
   * // Single event type
   * @Input() eventType: string = 'click';
   * 
   * // Multiple event types
   * @Input() eventType: string = 'click,mousedown,touchstart';
   * 
   * // Empty or undefined defaults to 'click'
   * @Input() eventType: string = '';
   * ```
   */
  @Input({ 
    alias: 'appStopPropagation',
    transform: (value: string) => {
      
      // if the value is not a string or is an empty string, return 'click'
      if (typeof value !== 'string' || value.length === 0) return 'click';

      // if the value is a string, return the value
      return value

    }
  })
  public eventType!: string;

  /**
   * Getter that converts the eventType string into an array of individual event names.
   * Splits the comma-separated string and trims whitespace from each event type.
   * 
   * @returns {string[]} Array of event type names
   * 
   * @example
   * ```typescript
   * // Input: 'click, mousedown, touchstart'
   * // Output: ['click', 'mousedown', 'touchstart']
   * 
   * // Input: 'click'
   * // Output: ['click']
   * 
   * // Input: ''
   * // Output: ['click'] (default)
   * ```
   */
  public get events(): string[] {
    return this.eventType.split(',').map((event: string) => event.trim());
  }

  /**
   * Constructor initializes the directive with required dependencies.
   * Renderer2 is used for safe DOM manipulation, while ElementRef provides
   * access to the host element for event listener attachment.
   * 
   * @param {Renderer2} _renderer - Angular renderer for safe DOM manipulation
   * @param {ElementRef} _elementRef - Reference to the host element
   * 
   * @example
   * ```typescript
   * constructor(
   *   private _renderer: Renderer2,
   *   private _elementRef: ElementRef
   * ) {}
   * ```
   */
  constructor(
    private _renderer: Renderer2,
    private _elementRef: ElementRef
  ) { }

  /**
   * Lifecycle hook called after the directive is initialized.
   * Sets up event listeners for each specified event type and ensures
   * at least one event listener is attached (defaults to 'click' if none specified).
   * 
   * @example
   * ```typescript
   * ngOnInit(): void {
   *   // Ensure at least one event type is specified
   *   if (this.events.length === 0) this.eventType = 'click';
   *   
   *   // Add listeners for each event type
   *   this.events.forEach((event: string) => this.addListener(event));
   * }
   * ```
   */
  ngOnInit(): void {

    // if the eventType is an empty string, set the eventType to 'click'
    if (this.events.length === 0) this.eventType = 'click';

    // add a listener for each event
    this.events.forEach((event: string) => this.addListener(event));
  }

  /**
   * Stops event propagation to parent elements.
   * Prevents the event from bubbling up the DOM tree while allowing
   * the event to be handled by the current element.
   * 
   * @param {Event} event - The DOM event object
   * 
   * @example
   * ```typescript
   * public stopPropagation(event: Event): void {
   *   event.stopPropagation();
   * }
   * 
   * // Usage in event handler
   * <button (click)="stopPropagation($event)">Click</button>
   * ```
   */
  public stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  /**
   * Stops immediate event propagation and prevents other event handlers
   * on the same element from executing. This is more aggressive than
   * stopPropagation as it stops all subsequent handlers.
   * 
   * @param {Event} event - The DOM event object
   * 
   * @example
   * ```typescript
   * public stopImmediatePropagation(event: Event): void {
   *   event.stopImmediatePropagation();
   * }
   * 
   * // Usage in event handler
   * <button (click)="stopImmediatePropagation($event)">Click</button>
   * ```
   */
  public stopImmediatePropagation(event: Event): void {
    event.stopImmediatePropagation();
  }

  /**
   * Adds an event listener to the host element for the specified event type.
   * Uses the Renderer2 for safe DOM manipulation and automatically calls
   * stopPropagation when the event occurs.
   * 
   * @param {string} event - The event type to listen for (e.g., 'click', 'mousedown')
   * 
   * @example
   * ```typescript
   * public addListener(event: string): void {
   *   this._renderer.listen(
   *     this._elementRef.nativeElement, 
   *     event, 
   *     this.stopPropagation
   *   );
   * }
   * 
   * // Adding multiple event listeners
   * ['click', 'mousedown', 'touchstart'].forEach(event => {
   *   this.addListener(event);
   * });
   * ```
   */
  public addListener(event: string): void {
    this._renderer.listen(this._elementRef.nativeElement, event, this.stopPropagation);
  }

  /**
   * Lifecycle hook called when the directive is destroyed.
   * Cleans up the Renderer2 instance to prevent memory leaks
   * and ensure proper resource management.
   * 
   * @example
   * ```typescript
   * ngOnDestroy(): void {
   *   this._renderer.destroy();
   * }
   * ```
   */
  ngOnDestroy(): void {
    this._renderer.destroy();
  }

}